import { useState, useEffect, useCallback } from 'react';
import { Download, X, Share } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSettings } from '@/contexts/SettingsContext';
import { useToast } from '@/hooks/use-toast';
import logoMark from '@/assets/logo-fennec-cart-mark.webp';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let globalDeferredPrompt: BeforeInstallPromptEvent | null = null;

// Capture the event as early as possible
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    globalDeferredPrompt = e as BeforeInstallPromptEvent;
  });
}

const PWAInstallPrompt = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const { language } = useSettings();
  const { toast } = useToast();

  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
  const isAr = language === 'ar';

  // Re-listen inside component to catch late events
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      globalDeferredPrompt = e as BeforeInstallPromptEvent;
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || (navigator as any).standalone === true;
    setIsStandalone(standalone);
    if (standalone) return;

    const dismissed = localStorage.getItem('pwa_install_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 3 * 24 * 60 * 60 * 1000) return;

    const timer = setTimeout(() => setShowBanner(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleInstallClick = useCallback(async () => {
    if (isInstalling) return;
    setIsInstalling(true);

    // Android / Chrome / Edge: Direct install prompt
    if (globalDeferredPrompt) {
      try {
        await globalDeferredPrompt.prompt();
        const { outcome } = await globalDeferredPrompt.userChoice;

        if (outcome === 'accepted') {
          setShowBanner(false);
          localStorage.setItem('pwa_install_dismissed', Date.now().toString());
        }

        globalDeferredPrompt = null;
        setIsInstalling(false);
        return;
      } catch {
        globalDeferredPrompt = null;
      }
    }

    // iOS Safari: No direct install API — show a brief, non-blocking toast
    if (isIOS) {
      toast({
        title: isAr ? '📲 لتثبيت التطبيق' : '📲 To install the app',
        description: isAr
          ? 'اضغط على زر المشاركة ⬆️ ثم "إضافة إلى الشاشة الرئيسية"'
          : 'Tap Share ⬆️ then "Add to Home Screen"',
        duration: 6000,
      });
    } else {
      // Other browsers without beforeinstallprompt
      toast({
        title: isAr ? '📲 لتثبيت التطبيق' : '📲 To install the app',
        description: isAr
          ? 'اضغط على ⋮ (قائمة المتصفح) ثم "تثبيت التطبيق"'
          : 'Tap ⋮ (browser menu) → "Install app"',
        duration: 6000,
      });
    }

    setIsInstalling(false);
  }, [isInstalling, isAr, isIOS, toast]);

  const handleDismiss = useCallback(() => {
    setShowBanner(false);
    localStorage.setItem('pwa_install_dismissed', Date.now().toString());
  }, []);

  if (isStandalone) return null;

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-20 left-2 right-2 z-50 max-w-lg mx-auto"
        >
          <div className="bg-card border border-border rounded-2xl shadow-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0 bg-primary/10 flex items-center justify-center">
                <img src={logoMark} alt="Marché DZ" className="w-10 h-10 object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-foreground">
                   📲 Marché DZ
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {isAr
                    ? 'ثبّت التطبيق على هاتفك للوصول السريع بدون متصفح'
                    : 'Install on your phone for quick access without a browser'}
                </p>

                {/* iOS instructions inline */}
                {isIOS && (
                  <div className="mt-2 text-xs text-muted-foreground bg-muted/50 rounded-lg p-2.5 space-y-1">
                    <p className="font-semibold text-foreground flex items-center gap-1">
                      <Share size={14} />
                      {isAr ? 'خطوات التثبيت:' : 'How to install:'}
                    </p>
                    <p>{isAr ? '1. اضغط على زر المشاركة ⬆️ أسفل الشاشة' : '1. Tap the Share button ⬆️ at the bottom'}</p>
                    <p>{isAr ? '2. اختر "إضافة إلى الشاشة الرئيسية"' : '2. Select "Add to Home Screen"'}</p>
                  </div>
                )}

                <button
                  onClick={handleInstallClick}
                  disabled={isInstalling}
                  className="mt-2 flex items-center gap-2 bg-[hsl(25,95%,40%)] text-white text-xs font-bold px-4 py-2.5 rounded-lg hover:opacity-90 transition-opacity w-full justify-center disabled:opacity-60"
                >
                  <Download size={16} />
                  {isInstalling
                    ? (isAr ? 'جارِ التثبيت...' : 'Installing...')
                    : isIOS
                      ? (isAr ? 'كيف أثبت التطبيق؟' : 'How to install?')
                      : (isAr ? 'تثبيت التطبيق الآن' : 'Install App Now')}
                </button>
              </div>
              <button onClick={handleDismiss} aria-label="Close" className="text-muted-foreground hover:text-foreground p-1 shrink-0">
                <X size={18} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PWAInstallPrompt;
